/**
 * Lightweight in-app activity logger.
 *
 * `logActivity(action, properties)` POSTs to /api/activity/log AND mirrors
 * to PostHog (if a key is configured). Both are non-blocking — failures are
 * swallowed so analytics never break business logic.
 *
 * `useActivityPageView()` is a hook that fires a `page_view` event on every
 * route change. Drop it into a top-level component (we use App.js).
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "./api";
import { capture } from "./posthog";

export function logActivity(action, properties = {}, path = null) {
  const effectivePath = path || window.location.pathname;
  // Mirror to PostHog (silent no-op when no key)
  capture(action, { ...properties, path: effectivePath });
  // Internal log. Auth is enforced via the HTTP-only `access_token` cookie
  // (sent automatically with withCredentials). If the user is anonymous the
  // backend returns 401 and we silently swallow it — analytics never throw.
  try {
    api.post("/activity/log", {
      action,
      path: effectivePath,
      properties,
    }).catch(() => { /* never throw for analytics */ });
  } catch { /* silent */ }
}

export function useActivityPageView() {
  const location = useLocation();
  useEffect(() => {
    logActivity("page_view", {}, location.pathname + (location.search || ""));
  }, [location.pathname, location.search]);
}
